import { useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  applyApproval,
  approvalQuery,
  type ApprovalPageData,
  TicketLinkGone,
  TicketLinkRefused,
} from '../api/easeedeskTicketLinks'
import { assetUrl } from '../ui/assetUrl'
import './TicketApprovalPage.css'

/**
 * `easeedesk/ticket/approve/<jeton>` si `easeedesk/ticket/deny/<jeton>`.
 *
 * Deschiderea adresei doar ARATA ce urmeaza sa se intample, cu un buton.
 * Nimic nu se schimba pana la apasare - iar asta nu e o alegere de stil: prima
 * varianta din portal aproba direct la deschidere, si tichetele au intrat in
 * "approved" in clipa in care a ajuns mailul, apasate de previzualizarea
 * clientului de mail. Cand omul apasa dupa cateva minute, jetonul era deja
 * consumat, si nici macar nu stia ca aprobase.
 *
 * Motivul respingerii nu e obligatoriu - un link care refuza sa lucreze pana nu
 * scrii ceva se abandoneaza - dar e cerut in cuvinte, fiindca "nu" fara motiv il
 * lasa pe cel care a cerut aprobarea sa ghiceasca.
 *
 * ASEZAREA. Omul deschide linkul de cele mai multe ori pe telefon, din clientul
 * de mail. De-aia: sigla sus, ca sa stie de unde vine pagina; detaliile
 * tichetului, ca sa nu hotarasca orbeste; un singur buton mare, pe toata
 * latimea. Pe telefon cardul sta lipit de margini (16px), fara golul de sus;
 * pe ecran mare e centrat, de 560px. Foaia e `TicketApprovalPage.css`, pe
 * tokenurile din `tokens.css`.
 *
 * Ce vine de la server se pune ca TEXT. Rezumatul descrierii pleaca deja
 * curatat de etichete (`ApprovalLinks::summary`), si aici nu se deseneaza ca
 * HTML nimic din ce a scris altcineva.
 */

type Action = 'approve' | 'deny'

/** Ce s-a intors dintr-o cerere care n-a mers, pe intelesul paginii. */
function problem(error: unknown): { gone: boolean; message: string } {
  if (error instanceof TicketLinkGone) return { gone: true, message: error.message }
  if (error instanceof TicketLinkRefused) return { gone: false, message: error.message }

  return { gone: false, message: 'Something went wrong. Please try again.' }
}

/* -------------------------------------------------------------------------
 * SCHELETUL: sigla, cardul, subsolul
 * ---------------------------------------------------------------------- */

function Frame({ provider, children }: { provider?: string; children: React.ReactNode }) {
  return (
    <div className="oda">
      <main className="oda-card">
        <header className="oda-brand">
          <img className="oda-logo" src={assetUrl('images/logo/OptimumDeskHeader.png')} alt="OptimumDesk" />
          <span className="oda-brand-text">
            {provider ? (
              <>
                <span className="oda-brand-kicker">Request from</span>
                <span className="oda-brand-name">{provider}</span>
              </>
            ) : (
              <span className="oda-brand-kicker">Ticket approval</span>
            )}
          </span>
        </header>
        <div className="oda-body">{children}</div>
        <footer className="oda-foot">
          <LockIcon />
          <span className="oda-foot-text">This is a personal link from your service desk. Do not forward it.</span>
        </footer>
      </main>
    </div>
  )
}

/* -------------------------------------------------------------------------
 * ICONITE - SVG in linie, fara biblioteca
 * ---------------------------------------------------------------------- */

function LockIcon() {
  return (
    <svg className="oda-foot-icon" width="13" height="13" viewBox="0 0 16 16" aria-hidden="true">
      <path
        d="M4.5 7V5a3.5 3.5 0 0 1 7 0v2h.5A1 1 0 0 1 13 8v6a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V8a1 1 0 0 1 1-1h.5Zm1.5 0h4V5a2 2 0 1 0-4 0v2Z"
        fill="currentColor"
      />
    </svg>
  )
}

function ResultIcon({ kind }: { kind: 'ok' | 'no' | 'gone' | 'warn' }) {
  const path = {
    ok: 'M7 12.5l3.2 3.2L17 9',
    no: 'M8.5 8.5l7 7M15.5 8.5l-7 7',
    gone: 'M12 7.5v5.5M12 16.5v.01',
    warn: 'M12 7.5v5.5M12 16.5v.01',
  }[kind]

  return (
    <div className={`oda-result-icon oda-result-icon--${kind}`} aria-hidden="true">
      <svg viewBox="0 0 24 24">
        <path d={path} fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </div>
  )
}

/* -------------------------------------------------------------------------
 * ECRANELE DE REZULTAT
 * ---------------------------------------------------------------------- */

function Result({
  kind,
  title,
  message,
  page,
  hint,
}: {
  kind: 'ok' | 'no' | 'gone' | 'warn'
  title: string
  message: string
  page?: ApprovalPageData
  hint?: string
}) {
  return (
    <Frame provider={page?.provider}>
      <div className="oda-result" role="status">
        <ResultIcon kind={kind} />
        <h1 className="oda-title oda-title--center">{title}</h1>
        <p className="oda-result-message">{message}</p>
        {page ? (
          <div className="oda-result-ticket">
            <span className="oda-ref">#{page.ticket}</span>
            {page.subject ? <span className="oda-result-subject">{page.subject}</span> : null}
          </div>
        ) : null}
        {hint ? <p className="oda-note oda-note--center">{hint}</p> : null}
      </div>
    </Frame>
  )
}

/**
 * Linkul nu deschide nimic. Un singur text pentru inexistent, expirat si
 * folosit - deosebirea i-ar spune unui strain ca a nimerit un jeton adevarat.
 */
function Gone() {
  return (
    <Result
      kind="gone"
      title="This link is no longer valid"
      message="It may have been used already, or it may have expired."
      hint="If a decision is still needed, reply to the ticket email or ask your service desk to send the link again."
    />
  )
}

/* -------------------------------------------------------------------------
 * DETALIILE TICHETULUI
 * ---------------------------------------------------------------------- */

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/**
 * `2026-09-01 09:05:00` -> `1 Sep 2026, 09:05`. Citit pe bucati, nu prin
 * `new Date()`: ora e a serverului, si o mutare pe fusul browserului ar arata
 * alta ora decat cea din email.
 */
export function readableDate(value: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2}))?/.exec(value)
  if (!m) return value

  const month = MONTHS[Number(m[2]) - 1]
  if (!month) return value

  const day = `${Number(m[3])} ${month} ${m[1]}`

  return m[4] ? `${day}, ${m[4]}:${m[5]}` : day
}

/** Culoarea prioritatii vine din baza; se foloseste numai cand e chiar o culoare. */
function safeColor(value: string): string | undefined {
  return /^#[0-9a-f]{3,8}$/i.test(value.trim()) ? value.trim() : undefined
}

function TicketDetails({ page }: { page: ApprovalPageData }) {
  const d = page.details
  const facts: Array<{ label: string; value: React.ReactNode }> = []

  if (d?.type) facts.push({ label: 'Type', value: d.type })
  if (d?.company) facts.push({ label: 'Company', value: d.company })
  if (d?.requester) facts.push({ label: 'Requested by', value: d.requester })
  if (d?.created) facts.push({ label: 'Created', value: readableDate(d.created) })
  if (d?.priority?.name) {
    const color = safeColor(d.priority.color)
    facts.push({
      label: 'Priority',
      value: (
        <span className="oda-priority">
          {color ? <span className="oda-priority-dot" style={{ background: color }} aria-hidden="true" /> : null}
          {d.priority.name}
        </span>
      ),
    })
  }

  return (
    <section className="oda-ticket" aria-label={`Ticket ${page.ticket}`}>
      <div className="oda-ticket-head">
        <span className="oda-ref">#{page.ticket}</span>
        <h2 className="oda-subject">{page.subject || 'Ticket without a subject'}</h2>
      </div>

      {facts.length > 0 ? (
        <dl className="oda-facts">
          {facts.map((fact) => (
            <div className="oda-fact" key={fact.label}>
              <dt>{fact.label}</dt>
              <dd>{fact.value}</dd>
            </div>
          ))}
        </dl>
      ) : null}

      {d?.summary ? (
        <div className="oda-summary">
          <div className="oda-summary-label">Description</div>
          <p className="oda-summary-text">{d.summary}</p>
        </div>
      ) : null}
    </section>
  )
}

/* -------------------------------------------------------------------------
 * PAGINA
 * ---------------------------------------------------------------------- */

export function TicketApprovalPage({ action, token }: { action: Action; token: string }) {
  const q = useQuery(approvalQuery(action, token))

  const [reason, setReason] = useState('')
  const [saving, setSaving] = useState(false)
  const [done, setDone] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [gone, setGone] = useState(false)
  /* Starea React se scrie abia la urmatoarea desenare; doua apasari in acelasi
     cadru ar trece amandoua de `saving`. Ref-ul se scrie pe loc. */
  const busy = useRef(false)

  const approve = action === 'approve'

  if (gone) return <Gone />
  if (done) {
    return (
      <Result
        kind={approve ? 'ok' : 'no'}
        title={approve ? 'Approved' : 'Declined'}
        message={done}
        page={q.data}
        hint="Your decision has been added to the ticket. You can close this page."
      />
    )
  }

  if (q.isError) {
    const what = problem(q.error)

    return what.gone ? <Gone /> : <Result kind="warn" title="This request cannot be completed" message={what.message} />
  }
  if (!q.data) {
    return (
      <Frame>
        <div className="oda-loading" role="status">
          <span className="oda-spinner oda-spinner--dark" aria-hidden="true" />
          Loading the request…
        </div>
      </Frame>
    )
  }

  const page = q.data

  async function submit(event: React.FormEvent) {
    event.preventDefault()
    if (busy.current) return

    busy.current = true
    setSaving(true)
    setError(null)
    try {
      const answer = await applyApproval(action, token, reason)
      setDone(answer.message)
    } catch (failed) {
      const what = problem(failed)
      if (what.gone) setGone(true)
      else setError(what.message)
    } finally {
      busy.current = false
      setSaving(false)
    }
  }

  return (
    <Frame provider={page.provider}>
      <span className={`oda-badge ${approve ? 'oda-badge--pending' : 'oda-badge--deny'}`}>
        <span className="oda-badge-dot" aria-hidden="true" />
        Awaiting your decision
      </span>
      <h1 className="oda-title">{approve ? 'Approval request' : 'Decline request'}</h1>
      <p className="oda-lead">
        {approve
          ? `${page.provider || 'Your service desk'} is asking you to approve the ticket below.`
          : `You are about to decline the ticket below. ${page.provider || 'Your service desk'} will be told.`}
      </p>

      <TicketDetails page={page} />

      {error ? (
        <div className="oda-error" role="alert">
          {error}
        </div>
      ) : null}

      <form className="oda-form" onSubmit={submit}>
        {approve ? null : (
          <div className="oda-field">
            <label htmlFor="reason" className="oda-label">
              Why are you declining it? <span className="oda-optional">(optional)</span>
            </label>
            <textarea
              id="reason"
              className="form-control oda-textarea"
              rows={4}
              placeholder="A short reason helps the requester understand your decision."
              value={reason}
              disabled={saving}
              onChange={(event) => setReason(event.target.value)}
            />
          </div>
        )}

        <button
          className={`oda-btn ${approve ? 'oda-btn--approve' : 'oda-btn--deny'}`}
          type="submit"
          disabled={saving}
          aria-busy={saving}
        >
          {saving ? <span className="oda-spinner" aria-hidden="true" /> : null}
          {saving ? (approve ? 'Approving…' : 'Declining…') : approve ? 'Approve' : 'Decline'}
        </button>

        <p className="oda-note oda-note--center">Nothing changes until you press the button.</p>
        <p className="oda-switch">
          {approve ? 'Not the right decision? ' : 'Changed your mind? '}
          <a href={`../${approve ? 'deny' : 'approve'}/${encodeURIComponent(token)}`}>
            {approve ? 'Decline instead' : 'Approve instead'}
          </a>
        </p>
      </form>
    </Frame>
  )
}
